/**
 * The tile mesh: a slightly-bevelled box with the procedural texture on its top face. [P5-tileart] [P5-board]
 *
 * ── Why rotation only ever touches the group, never the texture ─────────────────────────────────
 * `tile-texture.ts` always draws the tile UNROTATED. The top face is a `PlaneGeometry` rotated flat
 * with a fixed -90° turn about X; a point at plane-local `(X, Y, 0)` ends up at group-local
 * `(X, 0, -Y)`. Combined with the texture's UV convention (`u=0` west, `v=0` north), that means:
 *
 *     groupX = u - 0.5        groupZ = v - 0.5
 *
 * which is exactly `geometry.ts`'s `uvToLocal`. Placing the whole group at `(pos.x, 0, -pos.y)` and
 * rotating it by `-rotation * 90°` about Y reproduces the engine's clockwise rotation semantics exactly
 * (`edgeAt(def, rotation, side)`) — see the sign derivation kept in `.planning/lane-client-notes.md`.
 */

import {
  CanvasTexture,
  ExtrudeGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PlaneGeometry,
  Shape,
  SRGBColorSpace,
} from 'three';
import type { TileDef } from '@carc/engine';
import { renderTileTexture } from './tile-texture.js';
import { spriteReady, trySpriteCell } from './sprite-atlas.js';

export const TILE_SIZE = 1;
export const TILE_HEIGHT = 0.12;
const GROUND_GAP = 0.02;

let sharedShadowTexture: CanvasTexture | null = null;
function shadowTexture(): CanvasTexture {
  if (sharedShadowTexture) return sharedShadowTexture;
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  const g = ctx.createRadialGradient(64, 64, 8, 64, 64, 62);
  g.addColorStop(0, 'rgba(0,0,0,0.42)');
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  sharedShadowTexture = new CanvasTexture(canvas);
  return sharedShadowTexture;
}

/**
 * The tile body: a bevelled slab rather than a sharp-edged box, so the tile reads as a piece of cut
 * card catching the key light on its chamfer rather than a decal floating on a flat block. Built via
 * `ExtrudeGeometry` (no new dependency — `Shape`/`ExtrudeGeometry` are already used by
 * `meeple-mesh.ts`), then re-based to sit on y=0 via its own bounding box so the result is correct
 * regardless of which way `rotateX` happens to map the extrude axis — no hand-derived offset to get
 * wrong.
 */
let sharedBodyGeometry: ExtrudeGeometry | null = null;
function bodyGeometry(): ExtrudeGeometry {
  if (sharedBodyGeometry) return sharedBodyGeometry;
  const size = TILE_SIZE * 0.99;
  const half = size / 2;
  const shape = new Shape();
  shape.moveTo(-half, -half);
  shape.lineTo(half, -half);
  shape.lineTo(half, half);
  shape.lineTo(-half, half);
  shape.closePath();

  const bevel = TILE_SIZE * 0.018;
  const geo = new ExtrudeGeometry(shape, {
    depth: Math.max(0.01, TILE_HEIGHT - bevel * 2),
    bevelEnabled: true,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelSegments: 1,
    curveSegments: 1,
  });
  geo.rotateX(-Math.PI / 2);
  geo.computeBoundingBox();
  const min = geo.boundingBox?.min.y ?? 0;
  geo.translate(0, -min, 0);
  sharedBodyGeometry = geo;
  return sharedBodyGeometry;
}

export interface TileMesh {
  readonly group: Group;
  readonly defId: string;
  readonly topMaterial: MeshStandardMaterial;
  setHighlight(on: boolean, valid: boolean): void;
  /**
   * Release this tile's GPU and canvas memory.
   *
   * A tile is expensive: its top face is a 640x640 canvas (~1.6MB) uploaded as a mipmapped GPU
   * texture (~2.2MB). Detaching the group from the scene does NOT free any of that — three.js holds
   * geometries, materials and textures until they are disposed explicitly. The held tile is rebuilt
   * every single turn, so leaking it meant a fresh ~4MB per turn that nothing ever reclaimed, on top
   * of the tiles legitimately on the board. Over a long game that is hundreds of megabytes, and a
   * browser under memory pressure answers with `out of memory` thrown from the render loop.
   */
  dispose(): void;
}

/**
 * Build a tile's visual, unrotated and unplaced — the caller sets `group.position` / `group.rotation.y`.
 * `variationSeed` is normally the tile's `drawIndex`, so repeated tile types don't look identical.
 */
export function createTileMesh(def: TileDef, variationSeed: number): TileMesh {
  const group = new Group();
  group.name = `tile:${def.id}`;

  const shadow = new Mesh(
    new PlaneGeometry(TILE_SIZE * 1.32, TILE_SIZE * 1.32),
    new MeshBasicMaterial({ map: shadowTexture(), transparent: true, depthWrite: false }),
  );
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = -GROUND_GAP * 0.5;
  shadow.renderOrder = -1;
  group.add(shadow);

  const bodyMaterial = new MeshStandardMaterial({ color: '#6e5a3d', roughness: 0.9, metalness: 0.02 });
  const body = new Mesh(bodyGeometry(), bodyMaterial);
  body.castShadow = true;
  body.receiveShadow = true;
  group.add(body);

  const canvas = renderTileTexture(def, { variationSeed });
  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = 8;
  texture.needsUpdate = true;

  // [lane-sprites] The sprite sheet loads once, eagerly, but asynchronously — a tile placed before it
  // resolves gets the procedural fallback above. Upgrade it in place once art is available, rather
  // than leaving that one instance procedural for the rest of the game. No-ops (and never
  // resubscribes) once this def/seed already has art, so steady-state play never redraws twice.
  if (!trySpriteCell(def.id, variationSeed)) {
    void spriteReady.then(() => {
      if (!trySpriteCell(def.id, variationSeed)) return;
      texture.image = renderTileTexture(def, { variationSeed });
      texture.needsUpdate = true;
    });
  }

  const topMaterial = new MeshStandardMaterial({ map: texture, roughness: 0.88, metalness: 0 });
  const top = new Mesh(new PlaneGeometry(TILE_SIZE * 0.985, TILE_SIZE * 0.985), topMaterial);
  top.rotation.x = -Math.PI / 2;
  top.position.y = TILE_HEIGHT + 0.001;
  top.receiveShadow = true;
  group.add(top);

  let highlightMesh: Mesh | null = null;

  return {
    group,
    defId: def.id,
    topMaterial,
    dispose() {
      group.removeFromParent();
      if (highlightMesh) {
        highlightMesh.geometry.dispose();
        (highlightMesh.material as MeshBasicMaterial).dispose();
        highlightMesh = null;
      }
      top.geometry.dispose();
      topMaterial.dispose();
      texture.dispose();
      bodyMaterial.dispose();
      // `bodyGeometry()` is shared across every tile — disposing it would blank the whole board.
    },
    setHighlight(on: boolean, valid: boolean) {
      if (!on) {
        if (highlightMesh) {
          group.remove(highlightMesh);
          highlightMesh.geometry.dispose();
          (highlightMesh.material as MeshBasicMaterial).dispose();
          highlightMesh = null;
        }
        return;
      }
      const color = valid ? '#7fe08a' : '#e0645f';
      if (!highlightMesh) {
        highlightMesh = new Mesh(
          new PlaneGeometry(TILE_SIZE * 1.06, TILE_SIZE * 1.06),
          new MeshBasicMaterial({ color, transparent: true, opacity: 0.42, depthWrite: false }),
        );
        highlightMesh.rotation.x = -Math.PI / 2;
        highlightMesh.position.y = TILE_HEIGHT + 0.01;
        group.add(highlightMesh);
      } else {
        (highlightMesh.material as MeshBasicMaterial).color.set(color);
      }
    },
  };
}

export const worldPosition = (pos: { x: number; y: number }): [number, number, number] => [
  pos.x * TILE_SIZE,
  0,
  -pos.y * TILE_SIZE,
];

export const worldRotationY = (rotation: number): number => (-rotation * Math.PI) / 2;
